import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import logo from "../../assets/logo/giraffe-logo.svg";
import { authText } from "../../shared/i18n/authText";
import { useLocale } from "../../shared/lib/locale-context";
import { featuresQueryKey, fetchFeatures, meQueryKey } from "../../shared/session/queries";
import { LoginForm } from "./LoginForm";
import { safeReturnPath } from "./api";
import { PasskeySignInOption } from "./PasskeySignInOption";
import { usePasskeySignIn } from "./passkeySignIn";

/**
 * Sign-in. Returns to `next` (path + query, same-origin only). Batch 18 (GRF-46): with `Features__Passkeys` on and a browser
 * that can do WebAuthn, "Sign in with a passkey" under the password form, and the passkey offered in the e-mail autofill where
 * the browser supports it. `GET /features` is readable before sign-in.
 */
export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const next = safeReturnPath(params.get("next"));
  const features = useQuery({ queryKey: featuresQueryKey, queryFn: fetchFeatures, retry: false, staleTime: 60_000 });

  const locale = useLocale();
  const signedIn = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: meQueryKey });
    await navigate(next, { replace: true });
  }, [navigate, next, queryClient]);
  const passkey = usePasskeySignIn({ enabled: features.data?.Passkeys === true, locale, onSignedIn: signedIn });
  useEffect(() => {
    document.title = `${authText("loginSubmit", locale)} · Giraffe`;
  }, [locale]);

  return (
    <main className="gf-login" id="main">
      <div className="gf-login-card stack">
        <img src={logo} alt="Giraffe" width={140} height={36} />
        <h1>{authText("loginSubmit", locale)}</h1>
        <p className="sub">{authText("loginIntro", locale)}</p>
        <LoginForm onSignedIn={signedIn} passkeyAutofill={passkey.available} onPasswordStart={passkey.cancelWaiting} onPasswordFailed={passkey.resumeWaiting} />
        <PasskeySignInOption passkey={passkey} locale={locale} />
      </div>
    </main>
  );
}
